import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface FAQItem {
  id: number;
  question: string;
  answer: string;
  category: string;
}

const faqData: FAQItem[] = [
  {
    id: 1,
    question: 'What is Planning Poker?',
    answer: 'Planning Poker is an agile estimation technique where team members use cards to estimate the complexity of user stories. It helps teams reach consensus through discussion and promotes better understanding of requirements.',
    category: 'Basics'
  },
  {
    id: 2,
    question: 'How do I create a room?',
    answer: 'Simply click on \'Create Room\' from the home page, enter a room name, and you\'ll get a unique room ID. Share this ID with your team members so they can join your planning session.',
    category: 'Getting Started'
  },
  {
    id: 3,
    question: 'Can I use custom card values?',
    answer: 'Currently, we support the standard Fibonacci sequence (1, 2, 3, 5, 8, 13, 21) which is most commonly used in agile estimation. We\'re working on adding custom card deck support in future updates.',
    category: 'Features'
  },
  {
    id: 4,
    question: 'Is there a limit to the number of participants?',
    answer: 'No, there\'s no hard limit on the number of participants in a room. Our platform is designed to handle teams of any size, from small startups to large enterprise teams.',
    category: 'Features'
  },
  {
    id: 5,
    question: 'Do I need to create an account?',
    answer: 'No account is required! You can start using Scrumo immediately. Just create or join a room and enter your name. We believe in keeping things simple and friction-free.',
    category: 'Account'
  },
  {
    id: 6,
    question: 'How long are rooms active?',
    answer: 'Rooms remain active as long as there are participants in them. Once all participants leave, the room data is preserved for 24 hours, allowing teams to rejoin if needed.',
    category: 'Technical'
  },
  {
    id: 7,
    question: 'Can I export the estimation results?',
    answer: 'Yes! After completing your estimation session, you can export the results as a CSV file or copy them to your clipboard for easy integration with your project management tools.',
    category: 'Features'
  },
  {
    id: 8,
    question: 'Is my data secure?',
    answer: 'Absolutely. We use industry-standard encryption for all data transmission. Room data is stored temporarily and automatically deleted after sessions. We don\'t store any personal information.',
    category: 'Security'
  },
  {
    id: 9,
    question: 'Can I use Scrumo on mobile devices?',
    answer: 'Yes! Scrumo is fully responsive and works seamlessly on smartphones, tablets, and desktops. You can participate in planning sessions from any device with a web browser.',
    category: 'Technical'
  },
  {
    id: 10,
    question: 'How does the voting process work?',
    answer: 'Team members select their estimate cards privately. Once everyone has voted, the facilitator can reveal all votes simultaneously. This prevents anchoring bias and encourages independent thinking.',
    category: 'Process'
  }
];

const categories = ['All', 'Basics', 'Getting Started', 'Features', 'Account', 'Technical', 'Security', 'Process'];

const FAQ: React.FC = () => {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [expandedItems, setExpandedItems] = useState<number[]>([]);

  const filteredFAQs = selectedCategory === 'All'
    ? faqData
    : faqData.filter(item => item.category === selectedCategory);

  const toggleExpand = (id: number) => {
    setExpandedItems(prev =>
      prev.includes(id)
        ? prev.filter(item => item !== id)
        : [...prev, id]
    );
  };

  return (
    <div className="relative min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 overflow-hidden">
      {/* Background decorations */}
      <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 via-purple-600/20 to-pink-600/20 backdrop-blur-3xl"></div>
      <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%2523ffffff%22%20fill-opacity%3D%220.03%22%3E%3Ccircle%20cx%3D%2230%22%20cy%3D%2230%22%20r%3D%222%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-20"></div>

      {/* Navbar */}
      <nav className="relative z-10 flex items-center justify-between px-8 py-3">
        <div className="flex items-center gap-3">
          {/* Logo */}
          <div className="relative cursor-pointer" onClick={() => navigate('/')}>
            <div className="w-8 h-10 bg-white/20 rounded border border-white/30 flex items-center justify-center">
              <div className="relative">
                <div className="w-6 h-8 bg-blue-300 rounded-[2px] flex items-center justify-center">
                  <span className="text-white font-semibold text-xs">5</span>
                  <div className="absolute top-0.5 left-0.5 w-1.5 h-1.5 bg-white rounded-full"></div>
                  <div className="absolute bottom-0.5 right-0.5 w-1.5 h-1.5 bg-white rounded-full"></div>
                </div>
              </div>
            </div>
            <div className="absolute -right-1 -bottom-1 w-8 h-10 bg-white/20 rounded border border-white/30 flex items-center justify-center -rotate-6">
              <div className="relative">
                <div className="w-6 h-8 bg-gray-800 rounded-[2px] flex items-center justify-center">
                  <span className="text-white font-semibold text-2xl">1</span>
                  <div className="absolute top-0.5 left-0.5 w-1.5 h-1.5 bg-white rounded-full"></div>
                  <div className="absolute bottom-0.5 right-0.5 w-1.5 h-1.5 bg-white rounded-full"></div>
                </div>
              </div>
            </div>
          </div>
          <span className="text-xl font-medium text-white ml-8 cursor-pointer" onClick={() => navigate('/')}>Scrumo</span>
        </div>

        {/* Nav Links */}
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/')} className="px-4 py-2 text-sm text-white hover:bg-white/20 rounded-full border border-white/30 transition-colors">
            Home
          </button>
          <button onClick={() => navigate('/blog')} className="px-4 py-2 text-sm text-white hover:bg-white/20 rounded-full transition-colors">
            Blog
          </button>
          <button className="px-4 py-2 text-sm text-white bg-white/20 rounded-full border border-white/30">
            FAQ
          </button>
        </div>
      </nav>

      {/* Main Content */}
      <div className="relative z-10 px-8 py-12 max-w-5xl mx-auto">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <h1 className="text-5xl font-bold text-white mb-4">
            Frequently Asked <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-blue-400">Questions</span>
          </h1>
          <p className="text-xl text-purple-200 max-w-2xl mx-auto">
            Everything you need to know about using Scrumo for your agile planning sessions.
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                selectedCategory === category
                  ? 'bg-gradient-to-r from-purple-500 to-blue-500 text-white border border-transparent'
                  : 'bg-white/10 text-purple-200 border border-white/20 hover:bg-white/20 hover:border-white/30'
              }`}
            >
              {category}
            </button>
          ))}
        </div>

        {/* FAQ Items */}
        <div className="space-y-4">
          {filteredFAQs.map((item) => (
            <div
              key={item.id}
              className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-xl overflow-hidden hover:border-white/30 transition-all duration-300"
            >
              <button
                onClick={() => toggleExpand(item.id)}
                className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-white/5 transition-colors"
              >
                <div className="flex-1 pr-4">
                  <h3 className="text-lg font-semibold text-white">{item.question}</h3>
                  <span className="inline-block mt-2 px-3 py-1 bg-purple-500/20 backdrop-blur-xl rounded-full text-xs font-semibold text-purple-200 border border-purple-500/30">
                    {item.category}
                  </span>
                </div>
                <div className={`transform transition-transform duration-200 ${expandedItems.includes(item.id) ? 'rotate-180' : ''}`}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-purple-300"/>
                  </svg>
                </div>
              </button>

              <div className={`overflow-hidden transition-all duration-300 ${
                expandedItems.includes(item.id) ? 'max-h-96' : 'max-h-0'
              }`}>
                <div className="px-6 pb-4 text-purple-200">
                  {item.answer}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Contact Section */}
        <div className="mt-24 text-center bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-12">
          <h2 className="text-3xl font-bold text-white mb-4">
            Still Have Questions?
          </h2>
          <p className="text-lg text-purple-200 mb-8 max-w-2xl mx-auto">
            Can't find what you're looking for? Our team is here to help you get the most out of your planning sessions.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="px-8 py-3 bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-lg font-medium hover:from-purple-600 hover:to-blue-600 transition-colors">
              Contact Support
            </button>
            <button className="px-8 py-3 bg-white/10 text-white border border-white/30 rounded-lg font-medium hover:bg-white/20 transition-colors">
              Join Community
            </button>
          </div>
        </div>

        {/* Decorative Elements */}
        <div className="absolute top-32 right-20 opacity-20 pointer-events-none">
          <div className="w-20 h-24 bg-white/10 rounded border-2 border-white/20 flex items-center justify-center rotate-12">
            <span className="text-4xl font-bold text-white">?</span>
          </div>
        </div>

        <div className="absolute bottom-48 left-20 opacity-20 pointer-events-none">
          <div className="w-16 h-20 bg-white/10 rounded border-2 border-white/20 flex items-center justify-center -rotate-12">
            <span className="text-3xl font-bold text-white">!</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FAQ;
